import Head from 'next/head';
import React, { useEffect, useState } from 'react';
import RevampedBlogs from '../../components/Blogs/RevampedBlogs/RevampedBlogs';
import axios from 'axios';
import Image from 'next/image';
import router from 'next/router';
import { replaceTripshepherdWithSeeSight } from '../../utils/trip-sheppered-text';

export default function BlogsPage({ blogData }: BlogsPageProps) {
  const [blogs, setBlogs] = useState<CategoryBlogPost[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [cityId, setCityId] = useState<string | null>('all-blogs');
  const [cardsLimit, setCardsLimit] = useState(15);
  const [allBlogs, setAllBlogs] = useState<CategoryBlogPost[]>([]); // For Latest Blogs

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      // Set cardsLimit based on screen size
      if (screenWidth <= 1536) {
        setCardsLimit(12);
      } else {
        setCardsLimit(15);
      }
    };

    // Initial check to set the correct limit
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.get(
          `${process.env.NEXT_PUBLIC_FIREBASE_CONNECTION}/blogs`,
          {
            params: {
              order: 'desc',
              page: currentPage,
              limit: cardsLimit, // Use the updated cardsLimit
              ...(cityId && cityId !== 'all-blogs' && { city: cityId })
            }
          }
        );

        setBlogs(response.data.blogs);
        setTotalPages(response.data.totalPages);

        if (cityId === 'all-blogs' && allBlogs.length === 0) {
          setAllBlogs(response.data.blogs);
        }
      } catch (error) {
        console.error('Error fetching paginated blogs:', error);
      }
    };

    fetchBlogs();
  }, [currentPage, cityId, cardsLimit]);

  const handleCityChange = (newCityId: string) => {
    setCityId(newCityId);
    setCurrentPage(1);
  };

  return (
    <>
      <Head>
        <meta
          name="og:title"
          content={replaceTripshepherdWithSeeSight(blogData?.meta_title)}
        />
        <meta
          name="og:description"
          content={replaceTripshepherdWithSeeSight(blogData?.meta_description)}
          key="metadescription"
        />
        <title>{replaceTripshepherdWithSeeSight(blogData?.meta_title)}</title>
        <meta name="og:image" content="/vertical-seesight-logo.png" />
        <meta
          name="description"
          content={replaceTripshepherdWithSeeSight(blogData?.meta_description)}
          key="metadescription"
        />
        <link
          href={`https://www.seesight-tours.com/blog`}
          rel="canonical"
          key="canonical"
        />
      </Head>
      {blogData ? (
        <RevampedBlogs
          blogData={blogData}
          blogs={blogs}
          allBlogs={allBlogs}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          onCityChange={handleCityChange}
          cityId={cityId}
        />
      ) : (
        <div className="flex h-[calc(100vh-100px)] justify-center items-center gap-5 flex-col px-10 xl:mr-32">
          <Image
            src="/missing-data-illustration.svg"
            alt="missing data icon"
            width={272}
            height={181}
            className="w-[200px] h-[150px] sm:w-[272px] sm:h-[181px]"
          />
          <h2 className="text-black13 text-xs sm:text-base lg:text-lg font-semibold leading-none">
            No Blogs Data Found!
          </h2>
          <div className="text-center text-gray75 text-xs sm:text-base font-normal max-w-[400px]">
            We couldn&apos;t find information about the blogs. Please check back
            later or explore other content.
          </div>
          <button
            className="h-[32] bg-primary text-white text-center text-xs sm:text-sm px-5 rounded-[30px] py-2 sm:py-3 shadow"
            onClick={() => router.push('/')}
          >
            Explore Tours
          </button>
        </div>
      )}
    </>
  );
}

export async function getStaticProps() {
  try {
    const response = await axios.get<BLOGS_PAGE_DATA>(
      `${process.env.NEXT_PUBLIC_FIREBASE_CONNECTION}/blog-page`
    );
    let blogData = response.data;

    const updatedCities = [
      { name: 'All Blogs', id: 'all-blogs' },
      ...blogData.cities
    ];

    blogData = { ...blogData, cities: updatedCities };

    return {
      props: {
        blogData:
          blogData &&
          replaceTripshepherdWithSeeSight(JSON.parse(JSON.stringify(blogData)))
      },
      revalidate: 3600
    };
  } catch (error) {
    console.error('Error fetching blog data:', error);
    return {
      props: {
        blogData: null
      }
    };
  }
}

interface BlogImage {
  alternativeText?: string;
  url: string;
}

export interface BlogCity {
  name: string;
  id: string;
}

export interface BlogPost {
  page_title: string;
  publication_date: string;
  created_at: string;
  slug: string;
  snippet: string;
}

export interface TrendingBlogPost extends BlogPost {
  image: BlogImage;
}

export interface CategoryBlogPost extends BlogPost {
  image: string;
  category?: string;
  author?: string;
}

export interface BlogCategoryImage {
  image: string;
  name: string;
  id: string;
}

export interface Author {
  name: string;
  image?: string;
  tagLine?: string;
  slug: string;
}

export interface BLOGS_PAGE_DATA {
  trending: TrendingBlogPost[];
  latest: TrendingBlogPost[];
  blog_categories: BlogCategoryImage[];
  authors: Author[];
  page_tile: string;
  intro_paragragh: string;
  banner_image: string;
  meta_description: string;
  meta_title: string;
  cities: BlogCity[];
}

export interface BlogsPageProps {
  blogData: BLOGS_PAGE_DATA;
  blogs?: CategoryBlogPost[];
  allBlogs?: CategoryBlogPost[];
  currentPage: number;
  totalPages: number;
  onPageChange: React.Dispatch<React.SetStateAction<number>>;
  onCityChange: (newCityId: string) => void;
  cityId?: string | null;
}
